// ── Shared billing types and helpers ─────────────────────────────────────────

export interface TierItem {
  upTo: number | null;
  unitAmount: number;
  flatAmount: number | null;
}

export interface PlanPrice {
  id: string;
  nickname: string | null;
  unitAmount: number | null;
  currency: string;
  interval: string;
  billingScheme: string;
  isFlatRate: boolean;
  tiersMode: string | null;
  tiers: TierItem[] | null;
}

export interface AvailablePlan {
  id: string;
  name: string;
  description: string | null;
  trialDays: number | null;
  features?: string[];
  maxUsers?: number | null;
  prices: PlanPrice[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function intervalLabel(interval: string): string {
  return interval === 'year' ? 'yr' : 'mo';
}

export function intervalLabelFull(interval: string): string {
  return interval === 'year' ? 'Annual' : 'Monthly';
}

/**
 * Calculate the total cost for a given quantity with tiered pricing.
 * Supports both "volume" (single tier for total qty) and "graduated" (stacked tiers).
 */
export function calculateTieredTotal(tiers: TierItem[], quantity: number, mode: string | null): number {
  if (mode === 'volume') {
    for (const tier of tiers) {
      if (tier.upTo === null || quantity <= tier.upTo) {
        return quantity * tier.unitAmount;
      }
    }
    return quantity * tiers[tiers.length - 1].unitAmount;
  }

  // Graduated pricing
  let total = 0;
  let remaining = quantity;
  let prevUpTo = 0;

  for (const tier of tiers) {
    if (remaining <= 0) break;
    const tierSize = tier.upTo === null ? remaining : Math.min(remaining, tier.upTo - prevUpTo);
    total += tierSize * tier.unitAmount;
    remaining -= tierSize;
    prevUpTo = tier.upTo ?? prevUpTo + tierSize;
  }

  return total;
}

// ── Coupons ────────────────────────────────────────────────────────────────

/** Shape returned by POST /billing/validate-coupon. */
export interface CouponDetails {
  code: string;
  couponName: string;
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: 'once' | 'repeating' | 'forever';
  durationInMonths: number | null;
  appliesToProducts: string[] | null;
}

/** "50% off" or "$10.00 off". */
export function describeCouponDiscount(c: Pick<CouponDetails, 'percentOff' | 'amountOff'>): string {
  if (c.percentOff) return `${c.percentOff}% off`;
  if (c.amountOff) return `${formatCents(c.amountOff)} off`;
  return '';
}

/** "forever", "for 3 months", or "on your first payment". */
export function describeCouponDuration(c: Pick<CouponDetails, 'duration' | 'durationInMonths'>): string {
  if (c.duration === 'forever') return 'forever';
  if (c.duration === 'repeating' && c.durationInMonths) {
    return `for ${c.durationInMonths} month${c.durationInMonths === 1 ? '' : 's'}`;
  }
  return 'on your first payment';
}

/** Price in cents after the coupon, never below zero. */
export function applyCouponToCents(cents: number, c: Pick<CouponDetails, 'percentOff' | 'amountOff'>): number {
  if (c.percentOff) return Math.max(0, Math.round(cents * (1 - c.percentOff / 100)));
  if (c.amountOff) return Math.max(0, cents - c.amountOff);
  return cents;
}

/** True when the coupon is restricted to products that do not include `productId`. */
export function couponExcludesProduct(c: Pick<CouponDetails, 'appliesToProducts'>, productId: string): boolean {
  return Array.isArray(c.appliesToProducts) && !c.appliesToProducts.includes(productId);
}
